import { useState, useCallback } from 'react';

export interface UseSelectionUtils<T> {
  add: (currentId: T) => void;
  addMultiple: (currentIds: T[]) => void;
  toggle: (currentId: T) => void;
  deleteOne: (currentId: T) => void;
  deleteMultiple: (currentIds: T[]) => void;
  deleteCurrentAndAfter: (currentId: T) => void;
  has: (currentId: T) => boolean;
  reset: () => void;
  isEmpty: () => boolean;
}

export interface UseSelectionReturn<T> {
  selectedIds: T[];
  utils: UseSelectionUtils<T>;
}

export const useSelectionIds = <T extends string | number>(
  initialIds: T[],
): UseSelectionReturn<T> => {
  const [selectedIds, setSelectedIds] = useState<T[]>(initialIds);

  const add = useCallback((currentId: T) => {
    setSelectedIds((prevSelectedIds) => [...prevSelectedIds, currentId]);
  }, []);

  const addMultiple = useCallback((currentIds: T[]) => {
    setSelectedIds((prevSelectedIds) => {
      const set = new Set<T>([...prevSelectedIds, ...currentIds]);

      return Array.from(set);
    });
  }, []);

  const toggle = useCallback((currentId: T) => {
    setSelectedIds((prevSelectedIds) => {
      const set = new Set<T>(prevSelectedIds);

      if (set.has(currentId)) {
        set.delete(currentId);
      } else {
        set.add(currentId);
      }

      return Array.from(set);
    });
  }, []);

  const deleteOne = useCallback((currentId: T) => {
    setSelectedIds((prevSelectedIds) => {
      const filtersIds = prevSelectedIds.filter(
        (currentPrevId) => currentPrevId !== currentId,
      );

      return filtersIds;
    });
  }, []);

  const deleteMultiple = useCallback((currentIds: T[]) => {
    setSelectedIds((prevSelectedIds) => {
      const filtersIds = prevSelectedIds.filter(
        (currentPrevId) => !currentIds.includes(currentPrevId),
      );

      return filtersIds;
    });
  }, []);

  const deleteCurrentAndAfter = useCallback((currentId: T) => {
    setSelectedIds((prevSelectedIds) => {
      const currentIndex = prevSelectedIds.indexOf(currentId);

      if (currentIndex === -1) return prevSelectedIds;

      return prevSelectedIds.splice(0, currentIndex);
    });
  }, []);

  const has = useCallback(
    (currentId: T) => selectedIds.includes(currentId),
    [selectedIds],
  );

  const isEmpty = useCallback(() => selectedIds.length === 0, [selectedIds]);

  const reset = useCallback(() => {
    setSelectedIds(initialIds);
  }, [initialIds]);

  return {
    selectedIds,
    utils: {
      add,
      addMultiple,
      toggle,
      deleteOne,
      deleteMultiple,
      deleteCurrentAndAfter,
      has,
      isEmpty,
      reset,
    },
  };
};
